import { useEffect, useRef } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { useUserAuth } from '@/providers/UserAuthProvider';

/**
 * Hook that automatically signs in a connected wallet if it has an existing account
 */
export function useAutoWalletSignIn() {
  const { isConnected, address } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { user, signInWithWallet } = useUserAuth();
  const attemptedRef = useRef<string | null>(null);

  useEffect(() => {
    // Don't auto-sign if:
    // 1. Wallet not connected
    // 2. User already signed in
    // 3. Already attempted for this address
    if (!isConnected || !address || user || attemptedRef.current === address) {
      return;
    }

    const autoSignIn = async () => {
      try {
        // Mark this address as attempted
        attemptedRef.current = address;

        // Check if this wallet has an existing account
        const checkResponse = await fetch(`${window.location.origin}/.netlify/functions/check-wallet-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address }),
        });

        if (!checkResponse.ok) {
          // No existing account, don't auto-sign
          return;
        }

        const { exists } = await checkResponse.json();
        if (!exists) {
          // No existing account, don't auto-sign
          return;
        }

        // Account exists, auto-sign in
        console.log('Auto-signing in wallet:', address);
        const nonce = Date.now();
        const message = `MdbApp Wallet Login\nAddress: ${address}\nNonce: ${nonce}`;
        const signature = await signMessageAsync({ message });
        await signInWithWallet(address, signature, message);
        console.log('Auto-sign in successful');
      } catch (error) {
        console.error('Auto-sign in failed:', error);
        // Silently fail - user can manually sign in if needed
      }
    };

    // Small delay to avoid race conditions
    const timer = setTimeout(autoSignIn, 500);
    return () => clearTimeout(timer);
  }, [isConnected, address, user, signMessageAsync, signInWithWallet]);
}
